import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Lenis from "lenis";
import { Button } from "@/components/ui/button";
import {
  Heart, PawPrint, Route, ShieldCheck, MapPin, Mail, Phone, Instagram,
  AlertTriangle, CheckCircle2, Utensils, Stethoscope, Activity, Users, ArrowRight, Quote
} from "lucide-react";
import DonateModal from "@/components/DonateModal";

const EASE = [0.22, 0.61, 0.21, 1];

// Hero & section imagery (sourced via design agent — Unsplash/Pexels)
const HERO_IMG = "https://customer-assets.emergentagent.com/job_bhoori-mission/artifacts/k8bq0822_Screenshot%202025-12-06%20at%2011.26.57%20AM.png";
const HERO_VIDEO = "https://customer-assets.emergentagent.com/job_bhoori-mission/artifacts/inv2zp67_WhatsApp%20Video%202026-05-10%20at%208.13.25%20PM.mp4";
const FEED_VIDEO = "https://customer-assets.emergentagent.com/job_bhoori-mission/artifacts/1wewuhx0_WhatsApp%20Video%202026-05-10%20at%208.15.09%20PM.mp4";
const GALLERY_VIDEO = "https://customer-assets.emergentagent.com/job_bhoori-mission/artifacts/d5stc53z_WhatsApp%20Video%202026-05-10%20at%208.16.13%20PM.mp4";
const GALLERY_VIDEO_2 = "https://customer-assets.emergentagent.com/job_bhoori-mission/artifacts/gj7t20s0_WhatsApp%20Video%202026-05-10%20at%207.58.07%20PM.mp4";
const GALLERY_VIDEO_3 = "https://customer-assets.emergentagent.com/job_bhoori-mission/artifacts/qaeevgjv_WhatsApp%20Video%202026-05-10%20at%207.59.07%20PM.mp4";
const GALLERY_VIDEO_4 = "https://customer-assets.emergentagent.com/job_bhoori-mission/artifacts/owpicf13_WhatsApp%20Video%202026-05-12%20at%201.01.15%20PM.mp4";
const GALLERY_VIDEO_5 = "https://customer-assets.emergentagent.com/job_bhoori-mission/artifacts/t5zhk89c_WhatsApp%20Video%202026-05-12%20at%201.03.50%20PM.mp4";
const GALLERY_VIDEO_6 = "https://customer-assets.emergentagent.com/job_bhoori-mission/artifacts/lnw6oskv_WhatsApp%20Video%202026-05-12%20at%2012.58.03%20PM.mp4";
const GALLERY_VIDEO_7 = "https://customer-assets.emergentagent.com/job_bhoori-mission/artifacts/nvtgxyh3_WhatsApp%20Video%202026-05-12%20at%2012.59.48%20PM.mp4";
const SURBHI_PHOTO = "https://customer-assets.emergentagent.com/job_bhoori-mission/artifacts/aqsmfg7w_2354bbd8-0970-4023-b2fb-05e0ab844d58.jpeg";
const PHOTO_NEW_1 = "https://customer-assets.emergentagent.com/job_bhoori-mission/artifacts/wdu9ivsw_WhatsApp%20Image%202026-05-10%20at%208.13.44%20PM.jpeg";
const PHOTO_NEW_2 = "https://customer-assets.emergentagent.com/job_bhoori-mission/artifacts/ko0flglm_WhatsApp%20Image%202026-05-10%20at%208.14.25%20PM.jpeg";
const FOUNDER_IMG = "https://customer-assets.emergentagent.com/job_bhoori-mission/artifacts/k8bq0822_Screenshot%202025-12-06%20at%2011.26.57%20AM.png";

const GALLERY = [
  { type: "video", src: GALLERY_VIDEO, alt: "Daytime feeding on the route", span: "row-span-2" },
  { type: "image", src: PHOTO_NEW_1, alt: "Surbhi with a recovering dog", span: "" },
  { type: "video", src: GALLERY_VIDEO_2, alt: "On the route", span: "" },
  { type: "image", src: PHOTO_NEW_2, alt: "Field moment from the routes", span: "row-span-2" },
  { type: "video", src: GALLERY_VIDEO_4, alt: "On the route", span: "" },
  { type: "video", src: GALLERY_VIDEO_3, alt: "On the route", span: "" },
  { type: "image", src: "https://customer-assets.emergentagent.com/job_bhoori-mission/artifacts/jvpe0b0e_Screenshot%202025-12-06%20at%2011.26.33%20AM.png", alt: "Night feeding route, multiple dogs gather", span: "" },
  { type: "video", src: GALLERY_VIDEO_5, alt: "On the route", span: "row-span-2" },
  { type: "image", src: "https://customer-assets.emergentagent.com/job_bhoori-mission/artifacts/k8bq0822_Screenshot%202025-12-06%20at%2011.26.57%20AM.png", alt: "A familiar dog approaches the meal bucket", span: "" },
  { type: "video", src: GALLERY_VIDEO_6, alt: "On the route", span: "" },
  { type: "image", src: "https://customer-assets.emergentagent.com/job_bhoori-mission/artifacts/brbnd5d0_Screenshot%202025-12-06%20at%2011.27.06%20AM.png", alt: "Puppies and adult dog crowd around the feeding spot", span: "" },
  { type: "video", src: GALLERY_VIDEO_7, alt: "On the route", span: "" },
];

// Kinetic hero — masked line-by-line reveal
const HERO_LINES = [
  <>Every night, over</>,
  <><span className="italic text-[#B8743C]">170+ free roaming</span></>,
  <><span className="italic text-[#B8743C]">indie dogs,</span> the ones</>,
  <>who live on our streets and</>,
  <>nowhere else, wait for the</>,
  <>same bucket, the same hands,</>,
  <>and the same promise that</>,
  <>someone will come.</>,
];

const MARQUEE_ITEMS = [
  "Feeding is infrastructure",
  "Same hands, same routes, every day",
  "Dignity, not pity",
  "Eight routes, one promise",
  "On the ground since 2020",
];

// Cursor spotlight tracker for gallery frames
function handleSpot(e) {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
}

function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const els = ref.current?.querySelectorAll(".kb-reveal") || [];
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("kb-in")),
      { threshold: 0.12 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
  return ref;
}

export default function Landing() {
  const [donateOpen, setDonateOpen] = useState(false);
  const [donatePrefill, setDonatePrefill] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const containerRef = useReveal();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroVidY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const heroVidRotate = useTransform(scrollYProgress, [0, 1], [0, 2]);

  // Lenis smooth momentum scrolling + smooth anchor navigation
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let rafId;
    const loop = (time) => { lenis.raf(time); rafId = requestAnimationFrame(loop); };
    rafId = requestAnimationFrame(loop);
    const onClick = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const id = a.getAttribute("href");
      if (id && id.length > 1) {
        const el = document.querySelector(id);
        if (el) {
          e.preventDefault();
          lenis.scrollTo(el, { offset: -150 });
        }
      }
    };
    document.addEventListener("click", onClick);
    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener("click", onClick);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openDonate = (prefill = null) => {
    setDonatePrefill(prefill);
    setDonateOpen(true);
  };

  const openQuick = (amount) => openDonate({
    id: `quick-${amount}`,
    label: "One time gift",
    amount: `₹${amount.toLocaleString("en-IN")}`,
    amount_inr: amount,
    _jumpToPay: true,
  });

  const openCustom = () => openDonate({ id: "custom", label: "Custom Amount", amount: "Any", _jumpToPay: true });

  return (
    <div ref={containerRef} className="bg-[#F6F2EA] text-[#2A1F1A] min-h-screen">
      {/* NAV */}
      <motion.header
        data-testid="site-header"
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: EASE }}
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
          scrolled ? "bg-[#F6F2EA]/90 backdrop-blur-md border-b border-[#DED2C2]/60" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-36 md:h-44 flex items-center justify-between">
          <a href="#top" data-testid="nav-logo" className="flex items-center gap-2">
            <img
              src="https://customer-assets.emergentagent.com/job_bhoori-mission/artifacts/tu01wm1x_WhatsApp%20Image%202025-11-22%20at%209.50.48%20PM.jpeg"
              alt="Kaalu Bhoori Foundation"
              className="h-28 md:h-36 w-auto object-contain"
              style={{ mixBlendMode: "multiply" }}
            />
            <span className="sr-only">Kaalu Bhoori Foundation</span>
          </a>
          <nav className="hidden md:flex items-center gap-6 text-sm text-[#5C4E46]">
            <a href="#problem" className="kb-link" data-testid="nav-problem">The Problem</a>
            <a href="#how" className="kb-link" data-testid="nav-how">How it works</a>
            <a href="#routes" className="kb-link" data-testid="nav-routes">Routes</a>
            <a href="#impact" className="kb-link" data-testid="nav-impact">Impact</a>
            <a href="#straight-talk" className="kb-link" data-testid="nav-straight-talk">Myths & Facts</a>
            <a href="#gallery" className="kb-link" data-testid="nav-gallery">Gallery</a>
            <a href="#contact" className="kb-link" data-testid="nav-contact">Contact</a>
          </nav>
          <Button
            data-testid="nav-donate-btn"
            onClick={() => openDonate()}
            className="bg-[#B8743C] hover:bg-[#99602F] text-[#F6F2EA] rounded-full px-5 h-10"
          >
            <Heart className="w-4 h-4 mr-2" /> Donate
          </Button>
        </div>
      </motion.header>

      {/* HERO — kinetic masked reveal + parallax video */}
      <section id="top" ref={heroRef} className="relative pt-52 md:pt-64 pb-24 md:pb-32 kb-grain overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div
              className="flex items-center gap-3 mb-6"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE }}
            >
              <span className="text-xs md:text-sm font-bold uppercase tracking-[0.22em] text-[#B8743C]">
                Indore · Madhya Pradesh
              </span>
              <span className="w-10 h-px bg-[#D4A373]"></span>
              <span className="text-xs uppercase tracking-[0.22em] text-[#5C4E46]">Established 2025</span>
            </motion.div>
            <h1
              data-testid="hero-heading"
              aria-label="Every night, over 170+ free roaming indie dogs, the ones who live on our streets and nowhere else, wait for the same bucket, the same hands, and the same promise that someone will come."
              className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.08]"
            >
              <span aria-hidden="true" className="hidden md:block">
                {HERO_LINES.map((line, i) => (
                  <span key={i} className="block overflow-hidden">
                    <motion.span
                      className="block will-change-transform"
                      initial={{ y: "112%" }}
                      animate={{ y: "0%" }}
                      transition={{ duration: 1.05, delay: 0.2 + i * 0.1, ease: EASE }}
                    >
                      {line}
                    </motion.span>
                  </span>
                ))}
              </span>
              <motion.span
                aria-hidden="true"
                className="block md:hidden"
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.3, ease: EASE }}
              >
                Every night, over
                <span className="italic text-[#B8743C]"> 170+ free roaming indie dogs, </span>
                the ones who live on our streets and nowhere else, wait for the same bucket, the same hands, and the same promise that someone will come.
              </motion.span>
            </h1>
            <motion.p
              className="mt-7 text-lg md:text-xl text-[#5C4E46] max-w-2xl leading-relaxed"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
            >
              Kaalu Bhoori Foundation operates eight daily feeding and monitoring routes
              across Indore, supporting sterilization, vaccination, rescue, recovery and
              long-term care for community dogs.
            </motion.p>
            <motion.div
              className="mt-10 flex flex-wrap items-center gap-4"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
            >
              <Button
                data-testid="hero-donate-btn"
                onClick={() => openDonate()}
                className="bg-[#B8743C] hover:bg-[#99602F] text-[#F6F2EA] rounded-full px-7 h-12 text-base shadow-[0_10px_30px_rgba(184,116,60,0.25)]"
              >
                <Heart className="w-4 h-4 mr-2" /> Support a route
              </Button>
              <a href="#impact" data-testid="hero-impact-link" className="inline-flex items-center gap-2 text-[#2A1F1A] hover:text-[#B8743C] transition-colors">
                <span className="border-b border-[#2A1F1A] hover:border-[#B8743C] pb-0.5">See the work</span>
                <ArrowRight className="w-4 h-4" />
              </a>
            </motion.div>

            <motion.div
              className="mt-12 flex flex-wrap items-center gap-2 md:gap-3 text-xs md:text-sm"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.3, ease: EASE }}
            >
              {[
                "On the routes since 2020",
                "Established in 2025",
                "12A & 80G registered",
                "170+ dogs monitored daily",
                "8 active routes",
              ].map((t) => (
                <span key={t} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#EFE8DA] border border-[#DED2C2] text-[#5C4E46]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#B8743C]"></span>
                  {t}
                </span>
              ))}
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <motion.div style={{ y: heroVidY, rotate: heroVidRotate }}>
              <motion.div
                className="relative"
                initial={{ opacity: 0, y: 70, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 1.2, delay: 0.5, ease: EASE }}
              >
                <div className="absolute -inset-6 bg-[#F2D5A3]/40 kb-blob -z-10"></div>
                <video
                  src={HERO_VIDEO}
                  poster={HERO_IMG}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="kb-blob w-full aspect-[4/5] object-cover shadow-[0_30px_60px_rgba(45,35,30,0.12)]"
                  data-testid="hero-video"
                />
                <motion.div
                  className="absolute -bottom-6 -left-6 bg-[#F6F2EA] border border-[#DED2C2] rounded-2xl px-5 py-4 shadow-[0_12px_30px_rgba(45,35,30,0.08)]"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 1.35, ease: EASE }}
                >
                  <div className="text-xs uppercase tracking-[0.2em] text-[#B8743C] font-bold">2025 so far</div>
                  <div className="font-display text-2xl text-[#2A1F1A] mt-0.5">56,000+ kg of food</div>
                  <div className="text-sm text-[#5C4E46]">across our eight routes</div>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* EDITORIAL MARQUEE */}
      <section aria-hidden="true" data-testid="manifesto-marquee" className="py-7 md:py-9 bg-[#F6F2EA] border-y border-[#DED2C2] overflow-hidden select-none">
        <div className="kb-marquee flex w-max items-center">
          {[0, 1].map((dup) => (
            <div key={dup} className="flex items-center shrink-0">
              {MARQUEE_ITEMS.map((t, i) => (
                <span key={`${dup}-${i}`} className="flex items-center">
                  <span className={`font-display text-3xl md:text-5xl tracking-tight whitespace-nowrap ${i % 2 ? "kb-outline" : "text-[#2A1F1A]"}`}>{t}</span>
                  <PawPrint className="w-5 h-5 md:w-6 md:h-6 mx-6 md:mx-10 text-[#B8743C] shrink-0" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* PROBLEM */}
      <section id="problem" className="py-24 md:py-32 bg-gradient-to-b from-[#EFE8DA] to-[#F6F2EA]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="kb-reveal">
            <div className="flex items-center gap-3 mb-5 text-[#A94442]">
              <AlertTriangle className="w-5 h-5" />
              <span className="text-xs font-bold uppercase tracking-[0.22em]">Chapter 01 · The Problem</span>
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
              Sterilization alone is not infrastructure. Continuity is.
            </h2>

            <div className="mt-7 grid grid-cols-3 gap-3">
              {[
                { v: "60M+", l: "free roaming community dogs across India, the largest such population in the world" },
                { v: "WHO", l: "endorses sterilization paired with anti rabies vaccination as the most effective long term approach" },
                { v: "<30%", l: "is the sterilization coverage achieved by most Indian ABC programs in published evaluations" },
              ].map((s) => (
                <div key={s.v} className="bg-white rounded-2xl p-4 border border-[#DED2C2]">
                  <div className="font-display text-2xl md:text-3xl text-[#B8743C] leading-none">{s.v}</div>
                  <div className="mt-2 text-xs text-[#5C4E46] leading-snug">{s.l}</div>
                </div>
              ))}
            </div>

            <div className="mt-10 space-y-5 text-[#5C4E46] text-base md:text-lg leading-relaxed">
              <p>
                India's cities are home to millions of community dogs, but long term systems
                for sterilization, vaccination, monitoring and follow up remain inconsistent
                across much of the country.
              </p>
              <p>
                Animal Birth Control (ABC) programs are essential, and global public health
                guidance consistently supports sterilization and anti rabies vaccination as
                the most effective long term approach to humane street dog population
                management and rabies control. But sterilization alone is not infrastructure.
                Continuity is.
              </p>
              <div className="rounded-2xl bg-white border border-[#DED2C2] p-6">
                <div className="text-xs font-bold uppercase tracking-[0.22em] text-[#B8743C] mb-3">ABC works best when surgery is paired with</div>
                <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-[#2A1F1A]">
                  {[
                    "anti rabies vaccination",
                    "post operative follow up",
                    "re identification",
                    "booster tracking",
                    "on ground monitoring over time",
                  ].map((p) => (
                    <li key={p} className="flex items-start gap-2.5">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#B8743C] shrink-0"></span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-sm text-[#5C4E46] italic">This is where many cities continue to struggle.</p>
              </div>
            </div>

            <div className="mt-10 space-y-7">
              {[
                { h: "Coverage gaps", b: "Catch and release operations often miss newly arrived, migrating, unapproachable or untraced dogs, allowing unsterilized populations to gradually return within the same territories." },
                { h: "Re identification at scale", b: "Ear notching is the standard visual marker used after sterilization, but without consistent field presence and accurate records, it becomes difficult to track who has been sterilized, vaccinated, boosted or medically followed up over time." },
                { h: "Post operative outcomes improve with continuity", b: "Daily route familiarity allows wounds, infections, weight loss, behavioural changes or surgical complications to be noticed earlier and monitored more consistently during recovery." },
                { h: "Vaccination effectiveness depends on continuity", b: "Anti rabies programs are strongest when the same dog populations are repeatedly monitored and revaccinated over time. Sporadic drives leave gaps in long term coverage and herd immunity." },
                { h: "Trust is infrastructure", b: "Dogs that recognize feeders and routes are easier to approach, monitor, vaccinate and transport for medical intervention. Without that daily relationship, even basic healthcare becomes harder to deliver consistently." },
              ].map((p) => (
                <div key={p.h} className="kb-reveal border-l-2 border-[#B8743C] pl-5 md:pl-6">
                  <h3 className="font-display text-xl md:text-2xl text-[#2A1F1A] leading-snug">{p.h}</h3>
                  <p className="mt-2 text-[#5C4E46] text-base md:text-lg leading-relaxed">{p.b}</p>
                </div>
              ))}
            </div>

            <p className="mt-10 text-[#5C4E46] text-base md:text-lg leading-relaxed">
              Many municipal systems across India remain under resourced relative to the scale
              and complexity of urban street dog management. Challenges including inconsistent
              coverage, contractor driven implementation, limited follow up capacity, weak data
              continuity and insufficient on ground monitoring continue to affect long term
              outcomes in many cities.
            </p>

            <div className="mt-8 rounded-2xl bg-[#2B1D18] text-[#F6F2EA] p-6 md:p-8">
              <div className="text-xs font-bold uppercase tracking-[0.22em] text-[#F2D5A3]">Where we come in</div>
              <p className="mt-3 text-base md:text-lg leading-relaxed text-[#E6DCC8]/90">
                Kaalu Bhoori Foundation's route based model exists to strengthen that missing
                continuity layer through daily presence, monitored feeding routes, repeat
                observation, vaccination support, recovery tracking and long term community
                familiarity.
              </p>
            </div>

            <p className="mt-6 text-xs text-[#5C4E46]/80 italic">
              Sources: World Health Organization (WHO) rabies guidance, Animal Welfare Board
              of India ABC Rules, peer reviewed evaluations of Animal Birth Control
              implementation in Indian cities, and studies on dog population management,
              vaccination continuity and urban rabies prevention.
            </p>
          </div>
        </div>
      </section>

      {/* SOLUTION */}
      <section className="py-24 md:py-32 bg-[#F6F2EA]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="kb-reveal">
            <div className="flex items-center gap-3 mb-5 text-[#6B8E6B]">
              <CheckCircle2 className="w-5 h-5" />
              <span className="text-xs font-bold uppercase tracking-[0.22em]">Chapter 02 · Our Solution</span>
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
              <span className="text-[#B8743C]">100% sterilization and vaccination</span> coverage on our routes.
            </h2>
            <p className="mt-6 text-[#5C4E46] text-base md:text-lg leading-relaxed">
              We work the same eight routes every single day. The dogs know us, we know them.
              That trust is what makes population control, vaccination and rescue actually
              possible at the scale a city like Indore needs.
            </p>
            <div className="mt-7 grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { icon: Utensils, label: "Daily feeding" },
                { icon: Stethoscope, label: "On route health" },
                { icon: ShieldCheck, label: "Vaccination" },
                { icon: Activity, label: "Emergency rescue" },
              ].map(({ icon: Ico, label }) => (
                <div key={label} className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 border border-[#DED2C2]">
                  <Ico className="w-4 h-4 text-[#B8743C]" />
                  <span className="text-sm text-[#2A1F1A]">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-10 mt-16 md:mt-20">
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-10 mt-10 md:mt-14">
          <div className="kb-reveal rounded-3xl bg-[#2A1F1A] text-[#F6F2EA] p-8 md:p-12 lg:p-14 border border-[#2A1F1A]">
            <div className="grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-7">
                <div className="flex items-center gap-3 mb-4 text-[#F2D5A3]">
                  <ShieldCheck className="w-5 h-5" />
                  <span className="text-xs font-bold uppercase tracking-[0.22em]">On rabies, plainly</span>
                </div>
                <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
                  Rabies is almost always fatal once symptoms begin. In humans, dogs, cats and other mammals.
                </h3>
                <p className="mt-5 text-[#E6DCC8]/85 text-base md:text-lg leading-relaxed">
                  The World Health Organization considers human and canine rabies effectively
                  untreatable once clinical symptoms appear, which is why prevention is the
                  entire game. A vaccinated dog cannot transmit rabies, and a vaccinated
                  neighbourhood cannot sustain it. That is why every dog on our routes is
                  vaccinated, and re vaccinated, every year.
                </p>
              </div>
              <div className="md:col-span-5">
                <div className="grid grid-cols-1 gap-3">
                  {[
                    { v: "100%", l: "fatal once symptoms begin, in every mammal it infects" },
                    { v: "1", l: "proven way to stop it: vaccination" },
                    { v: "153", l: "dogs vaccinated by us in 2025" },
                  ].map((s) => (
                    <div key={s.l} className="rounded-2xl bg-[#F6F2EA]/5 border border-[#F6F2EA]/10 p-4">
                      <div className="font-display text-3xl md:text-4xl text-[#F2D5A3]">{s.v}</div>
                      <div className="mt-1.5 text-xs text-[#E6DCC8]/80 leading-snug">{s.l}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="kb-reveal max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#B8743C]">Chapter 03 · How it works</span>
            <h2 className="mt-4 font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
              The Daily Feeding Route Model.
            </h2>
            <p className="mt-5 text-[#5C4E46] text-base md:text-lg leading-relaxed">
              Eight routes. Same time, same hands, every day. Care that is continuous, not
              occasional, is what turns a problem into a managed system.
            </p>
          </div>

          <div className="mt-16 grid md:grid-cols-3 gap-6 lg:gap-8">
            {[
              { n: "01", icon: Route, title: "Mapped routes, every dog known", desc: "Eight daily routes. Every dog under our care has a name, a story and a record. Vaccination dates, sterilization status and medical history are tracked, even though they don't carry an ID card." },
              { n: "02", icon: Utensils, title: "Daily feeding & monitoring", desc: "Nutritionally adequate meals served daily. Wounds, limps, behaviour changes are flagged on the spot." },
              { n: "03", icon: ShieldCheck, title: "Sterilize, vaccinate, deworm", desc: "Trust based catching makes sterilization and vaccination actually feasible. Post op care isn't optional, it is the work." },
              { n: "04", icon: Stethoscope, title: "Emergency interventions", desc: "Accidents, infections, untreated wounds, mange, eye injuries. Handled quickly with vets we trust, funded by donors we trust." },
              { n: "05", icon: Users, title: "Stability and predictability", desc: "Sterilization plus continuous monitoring prevents new births within the same territory, while daily feeding reduces food insecurity, scavenging injuries and resource based aggression. Peer reviewed studies on managed urban dog populations consistently show stable or declining numbers, fewer bites and improved welfare outcomes." },
              { n: "06", icon: Activity, title: "Transparent reporting", desc: "Every rupee tracked. Every intervention documented. Every donor sees the story they're funding." },
            ].map(({ n, icon: Ico, title, desc }, i) => (
              <motion.div
                key={n}
                initial={{ opacity: 0, y: 34 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease: EASE }}
                whileHover={{ y: -6 }}
                className="bg-[#EFE8DA] rounded-3xl p-7 md:p-8 border border-[#DED2C2] hover:border-[#D4A373] hover:shadow-[0_20px_40px_rgba(45,35,30,0.06)] transition-colors duration-300"
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="w-11 h-11 rounded-2xl bg-[#F6F2EA] grid place-items-center border border-[#DED2C2]">
                    <Ico className="w-5 h-5 text-[#B8743C]" />
                  </div>
                  <span className="font-display text-xl text-[#D4A373]">{n}</span>
                </div>
                <h3 className="font-display text-2xl text-[#2A1F1A] leading-snug">{title}</h3>
                <p className="mt-3 text-[#5C4E46] text-sm md:text-base leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* ON THE ROUTES — MAP PLACEHOLDER */}
      <section id="routes" className="py-24 md:py-32 bg-[#F6F2EA]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="kb-reveal max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#B8743C]">Chapter 04 · On the routes</span>
            <h2 className="mt-4 font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
              Eight routes across Indore, covering 16 active feeding and monitoring locations. Same hands, every day.
            </h2>
            <p className="mt-5 text-[#5C4E46] text-base md:text-lg leading-relaxed">
              Our daily feeding and monitoring rounds cover sixteen locations spread across the central and southern belt of the city.
            </p>
          </div>

          <div className="mt-12 kb-reveal rounded-3xl overflow-hidden border border-[#DED2C2] bg-white">
            <img
              src="https://customer-assets.emergentagent.com/job_bhoori-mission/artifacts/b2smfcu3_%231%20Nehru%20Stadium.png"
              alt="Indore city map with the 16 active feeding locations of Kaalu Bhoori Foundation"
              data-testid="route-map"
              className="w-full h-auto block"
            />
          </div>

          <div className="mt-8 kb-reveal">
            <div className="text-xs font-bold uppercase tracking-[0.22em] text-[#B8743C] mb-4">Active feeding locations</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2 text-sm text-[#2A1F1A]">
              {[
                "1. Nehru Stadium",
                "2. Parts of Radio Colony",
                "3. GPO and nearby",
                "4. Near Zoo",
                "5. Agrasen Chouraha",
                "6. Kaantaphod Mandir",
                "7. Shankar Bagh",
                "8. Along Chhavni Main Rd",
                "9. Sneh Nagar",
                "10. Agarwal Nagar Main Rd",
                "11. Sapna Sangeeta Main Rd",
                "12. Parts of Sindhi Colony",
                "13. Parts of Jairampur",
                "14. Medical College Main Rd",
                "15. Around Shreemaya",
                "16. Palsikar Colony Main Rd",
              ].map((loc) => (
                <div key={loc} className="flex items-start gap-2 py-1">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#B8743C] shrink-0"></span>
                  <span>{loc}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 kb-reveal flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-white border border-[#DED2C2] px-5 py-4">
            <div className="text-sm md:text-base text-[#2A1F1A] max-w-xl">
              Eight routes run every single day. Predictability is the reason they work.
            </div>
            <Button
              data-testid="routes-donate-btn"
              onClick={() => openDonate()}
              className="bg-[#B8743C] hover:bg-[#99602F] text-[#F6F2EA] rounded-full px-5 h-11"
            >
              <Heart className="w-4 h-4 mr-2" /> Sponsor a route
            </Button>
          </div>
        </div>
      </section>

      {/* INDIE PARIAH CELEBRATION */}
      <section id="indie" className="py-20 md:py-28 bg-[#2B1D18] text-[#F6F2EA]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#F2D5A3]">Interlude · A small thing worth saying</span>
          <h2 className="mt-5 font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
            Why do we call them street dogs?
            <span className="block italic text-[#F2D5A3] mt-2">Because they live on the street. That's it.</span>
          </h2>
          <p className="mt-7 text-base md:text-lg text-[#E6DCC8]/85 leading-relaxed max-w-2xl mx-auto">
            They are not strays. They are not problems. They are the
            <span className="text-[#F6F2EA] font-medium"> Indian Pariah </span>
            dog, an indigenous breed shaped by thousands of years of these very streets.
            Lean, smart, weatherproof, and unforgivably beautiful. We do not pity them. We
            celebrate them.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3 text-xs">
            {["Indigenous to the Indian subcontinent", "One of the oldest dog breeds on earth", "Built for the climate they live in", "Yours, mine, ours"].map((t) => (
              <span key={t} className="px-4 py-2 rounded-full bg-[#F6F2EA]/5 border border-[#F6F2EA]/15 text-[#E6DCC8]/85">{t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* WHY WE FEED */}
      <section id="feed" className="py-24 md:py-32 bg-[#EFE8DA]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="kb-reveal md:col-span-5">
            <div className="relative">
              <div className="absolute -inset-4 bg-[#F2D5A3]/50 kb-blob -z-10"></div>
              <video
                src={FEED_VIDEO}
                poster="https://customer-assets.emergentagent.com/job_bhoori-mission/artifacts/brbnd5d0_Screenshot%202025-12-06%20at%2011.27.06%20AM.png"
                autoPlay
                muted
                loop
                playsInline
                className="kb-blob w-full aspect-[4/5] object-cover shadow-[0_30px_60px_rgba(45,35,30,0.12)]"
              />
            </div>
          </div>
          <div className="kb-reveal md:col-span-7">
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#B8743C]">Chapter 05 · Why we feed</span>
            <h2 className="mt-4 font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
              Every dog deserves one clean meal a day.
            </h2>
            <p className="mt-5 text-[#5C4E46] text-base md:text-lg leading-relaxed">
              Feeding is not charity. It is infrastructure. A street dog surviving on garbage
              is sifting through broken glass, spoiled food, plastic and sharp waste. It breaks
              teeth, infects wounds and quietly kills. One consistent, clean meal changes all of that.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {[
                { t: "Health before everything", d: "A fed dog has a working immune system. Vaccinations work, wounds heal, recovery is faster." },
                { t: "Trust, earned daily", d: "The same bucket, the same hands, the same time. That is how a dog lets you catch it for treatment." },
                { t: "Calmer neighbourhoods", d: "Resource competition is what creates aggression. Predictable feeding ends it." },
                { t: "Dignity is not optional", d: "A meal that was actually cooked for them, not the scraps of someone else's lunch. It is a small thing. It is also the line between an animal that simply survives and an animal that is cared for." },
              ].map((p) => (
                <div key={p.t} className="rounded-2xl bg-[#F6F2EA] border border-[#DED2C2] p-5">
                  <div className="flex items-center gap-2 text-[#B8743C]">
                    <Utensils className="w-4 h-4" />
                    <div className="text-xs font-bold uppercase tracking-[0.22em]">{p.t}</div>
                  </div>
                  <p className="mt-2 text-sm text-[#5C4E46] leading-relaxed">{p.d}</p>
                </div>
              ))}
            </div>
            <p className="mt-8 text-sm md:text-base text-[#2A1F1A] italic">
              And no, feeding does not grow the population. Sterilization does that work. Feeding builds the trust that makes the sterilization, the vaccination and the medical care actually possible.
            </p>
          </div>
        </div>
      </section>


      {/* IMPACT */}
      <section id="impact" className="py-24 md:py-32 bg-[#2A1F1A] text-[#F6F2EA]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="kb-reveal max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#F2D5A3]">Chapter 06 · 2025 results</span>
            <h2 className="mt-4 font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
              Numbers that mean a dog ate today.
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#5C4E46]/40 rounded-3xl overflow-hidden">
            {[
              { val: "150", label: "Dogs under care" },
              { val: "6", label: "Daily routes" },
              { val: "100%", label: "Sterilization & vaccination" },
              { val: "56,000+", label: "Kg of food distributed" },
              { val: "55,000+", label: "Meals served" },
              { val: "153", label: "Vaccinations in 2025" },
              { val: "35", label: "Emergency interventions" },
              { val: "0", label: "Breeding incidents in zones" },
            ].map((s) => (
              <div key={s.label} className="bg-[#2A1F1A] p-7 md:p-9 kb-reveal">
                <div className="font-display text-4xl md:text-5xl lg:text-6xl font-bold leading-none tracking-tight">
                  <span className="text-[#F2D5A3]">{s.val}</span>
                </div>
                <div className="mt-3 text-sm md:text-base text-[#E6DCC8]/80">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="mt-12 kb-reveal flex flex-wrap items-center justify-between gap-4 border-t border-[#F6F2EA]/10 pt-8">
            <div className="text-sm text-[#E6DCC8]/80">Updated from active route logs. Reporting kept transparent and on file.</div>
            <Button
              data-testid="impact-donate-btn"
              onClick={() => openDonate()}
              className="bg-[#B8743C] hover:bg-[#99602F] text-[#F6F2EA] rounded-full px-5 h-11"
            >
              <Heart className="w-4 h-4 mr-2" /> Support the routes
            </Button>
          </div>
        </div>
      </section>
      <section id="straight-talk" className="py-24 md:py-32 bg-[#F6F2EA]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="kb-reveal max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#B8743C]">Chapter 07 · What people get wrong</span>
            <h2 className="mt-4 font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
              Myths and facts, told plainly.
            </h2>
            <p className="mt-5 text-[#5C4E46] text-base md:text-lg leading-relaxed">
              Most decisions around community dogs are made on assumption, not evidence.
              These are the things the field actually knows.
            </p>
          </div>

          <div className="mt-14 grid lg:grid-cols-3 gap-6 lg:gap-8">
            <div className="kb-reveal rounded-3xl bg-[#EFE8DA] border border-[#DED2C2] p-7 md:p-9">
              <div className="flex items-center gap-3 text-[#B8743C]">
                <ShieldCheck className="w-5 h-5" />
                <span className="text-xs font-bold uppercase tracking-[0.22em]">About rabies</span>
              </div>
              <h3 className="mt-4 font-display text-2xl md:text-3xl text-[#2A1F1A] leading-snug">Vaccination is the only way.</h3>
              <div className="mt-6 space-y-5">
                {[
                  { m: "Myth: Rabies can be treated if caught early.", t: "Truth: Once symptoms begin, rabies is 100% fatal in humans, dogs and cats. There is no cure. The vaccine is the only thing that prevents it." },
                  { m: "Myth: Only street dogs carry rabies.", t: "Truth: Any unvaccinated mammal can. The fastest way to make a neighbourhood rabies-free is to vaccinate the dogs that live in it." },
                  { m: "Myth: Even a vaccinated dog is a risk.", t: "Truth: A properly vaccinated dog cannot transmit rabies. The sterilized, vaccinated community dog on your lane is the safest dog in the city." },
                ].map((p, i) => (
                  <div key={i} className="border-t border-[#DED2C2] pt-5">
                    <div className="text-sm font-medium text-[#A94442]">{p.m}</div>
                    <div className="mt-1.5 text-sm text-[#2A1F1A] leading-relaxed">{p.t}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="kb-reveal rounded-3xl bg-[#EFE8DA] border border-[#DED2C2] p-7 md:p-9">
              <div className="flex items-center gap-3 text-[#B8743C]">
                <Utensils className="w-5 h-5" />
                <span className="text-xs font-bold uppercase tracking-[0.22em]">About feeding</span>
              </div>
              <h3 className="mt-4 font-display text-2xl md:text-3xl text-[#2A1F1A] leading-snug">Feeding stabilizes, never multiplies.</h3>
              <div className="mt-6 space-y-5">
                {[
                  { m: "Myth: Feeding street dogs grows the population.", t: "Truth: Population grows from breeding, not feeding. Consistent feeding paired with sterilization stabilizes numbers and ends the cycle." },
                  { m: "Myth: Dogs should just fend for themselves.", t: "Truth: Most 'fending for themselves' means eating glass, plastic and rotten food. One clean meal a day keeps a dog healthy, calm and disease-free." },
                  { m: "Myth: A fed dog becomes aggressive.", t: "Truth: It is the hungry, resource-competing dog that snaps. The same dog, fed predictably, becomes the gentle one that sleeps outside your gate." },
                ].map((p, i) => (
                  <div key={i} className="border-t border-[#DED2C2] pt-5">
                    <div className="text-sm font-medium text-[#A94442]">{p.m}</div>
                    <div className="mt-1.5 text-sm text-[#2A1F1A] leading-relaxed">{p.t}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="kb-reveal rounded-3xl bg-[#2A1F1A] text-[#F6F2EA] p-7 md:p-9">
              <div className="flex items-center gap-3 text-[#F2D5A3]">
                <Activity className="w-5 h-5" />
                <span className="text-xs font-bold uppercase tracking-[0.22em]">If you are bitten</span>
              </div>
              <h3 className="mt-4 font-display text-2xl md:text-3xl leading-snug">Do this, in order. Do not wait.</h3>
              <ol className="mt-6 space-y-5">
                {[
                  { n: "01", t: "Wash the wound immediately.", d: "Running water and soap. At least 15 minutes. This single step physically removes most of the rabies virus before it enters the nerves." },
                  { n: "02", t: "Go to a hospital within 24 hours.", d: "Ask for post-exposure prophylaxis (PEP), the full anti-rabies vaccination course, and immunoglobulin if recommended. It is free at most government hospitals in India." },
                  { n: "03", t: "Do not wait for symptoms.", d: "PEP works only before symptoms begin. Do not delay." },
                  { n: "04", t: "Tell us about the dog.", d: "Email or DM us with the location. We can identify the dog, confirm its vaccination, and monitor it. In almost every case on our routes, the dog was already vaccinated." },
                ].map((s) => (
                  <li key={s.n} className="border-t border-[#F6F2EA]/10 pt-5">
                    <div className="flex items-baseline gap-3">
                      <span className="font-display text-xl text-[#F2D5A3]">{s.n}</span>
                      <div className="text-[#F6F2EA] font-medium">{s.t}</div>
                    </div>
                    <p className="mt-1.5 text-sm text-[#E6DCC8]/85 leading-relaxed pl-8">{s.d}</p>
                  </li>
                ))}
              </ol>
              <a href="mailto:kaalubhoorifoundation@gmail.com" data-testid="bite-help-cta" className="mt-7 inline-flex items-center gap-2 text-[#F2D5A3] hover:text-[#F6F2EA] transition-colors">
                <Mail className="w-4 h-4" />
                <span className="border-b border-[#F2D5A3] hover:border-[#F6F2EA] pb-0.5">Email us: kaalubhoorifoundation@gmail.com</span>
              </a>
            </div>
          </div>
        </div>
      </section>


      {/* DONATION SECTION */}
      <section id="donate" className="py-24 md:py-32 bg-[#F6F2EA]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="kb-reveal max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#B8743C]">Chapter 08 · Choose what you want to support</span>
            <h2 className="mt-4 font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
              Consistent routes depend on consistent support.
            </h2>
            <p className="mt-5 text-[#5C4E46] text-base md:text-lg leading-relaxed">
              Pick what feels right. A one time gift, a sponsorship that funds a specific
              outcome, or a recurring contribution that keeps the routes running. All donations
              are tax deductible under Sections 12A and 80G.
            </p>
          </div>

          {/* Quick give module */}
          <div className="mt-12 kb-reveal rounded-3xl bg-white border border-[#DED2C2] p-6 md:p-8">
            <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
              <div className="text-xs font-bold uppercase tracking-[0.22em] text-[#B8743C]">Quick give</div>
              <div className="text-xs text-[#5C4E46]">One time. Pay by card, UPI, netbanking or wallet.</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[250, 500, 1000, 5000].map((a) => (
                <button
                  key={a}
                  data-testid={`page-quick-${a}`}
                  onClick={() => openQuick(a)}
                  className="rounded-2xl border border-[#DED2C2] bg-[#F6F2EA] px-4 py-5 hover:-translate-y-0.5 hover:border-[#B8743C] hover:bg-[#F2D5A3]/30 transition-all duration-200"
                >
                  <div className="font-display text-2xl md:text-3xl text-[#2A1F1A]">₹{a.toLocaleString("en-IN")}</div>
                </button>
              ))}
              <button
                data-testid="page-quick-custom"
                onClick={() => openCustom()}
                className="rounded-2xl bg-[#B8743C] hover:bg-[#99602F] text-[#F6F2EA] px-4 py-5 transition-all"
              >
                <div className="font-display text-xl md:text-2xl">Custom</div>
                <div className="text-xs opacity-85 mt-1">amount</div>
              </button>
            </div>
          </div>

          {/* Sponsorship cards */}
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {[
              { id: "feeding-day", title: "Sponsor a feeding day", amount: "₹3,000", cadence: "one time", desc: "Supports one full day of route feeding across our monitored community dog zones.", cta: "Sponsor a feeding day", tag: null },
              { id: "female-ster", title: "Female dog sterilization", amount: "₹4,500", cadence: "one time", desc: "Sterilization, vaccination, monitoring and recovery support for one female community dog.", cta: "Sponsor female sterilization", tag: null },
              { id: "male-ster", title: "Male dog sterilization", amount: "₹3,500", cadence: "one time", desc: "Sterilization, vaccination and monitored recovery for one male community dog.", cta: "Sponsor male sterilization", tag: null },
              { id: "monthly-dog", title: "Support one dog monthly", amount: "₹500", cadence: "per month", desc: "Recurring feeding, monitoring and basic care for one route dog. Continuity at its simplest.", cta: "Support one dog monthly", tag: "Recurring" },
              { id: "monthly-feeding", title: "Monthly feeding support", amount: "₹90,000", cadence: "per month", desc: "Covers one full month of route feeding support across all monitored zones, at ₹3,000 a day. Roughly 190 to 210 kilos of food prepared, served and tracked every single day, which adds up to about 5,700 to 6,300 kilos a month, plus the continuity of monitoring and care for the dogs who depend on these routes.", cta: "Sponsor monthly feeding", tag: "Most needed", featured: true },
              { id: "full-care", title: "Full inclusion into the route system", amount: "₹11,000", cadence: "per dog · year", desc: "Sterilization, vaccination, treatment, route integration and monitored follow up for one community dog for a full year. ₹5,000 covers a one time sterilization plus a year of vaccinations. ₹6,000 covers a year of feeding.", cta: "Sponsor full care and inclusion", tag: null },
            ].map((c, ci) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 36 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.75, delay: (ci % 3) * 0.08, ease: EASE }}
                whileHover={c.featured ? undefined : { y: -6 }}
                className={`rounded-3xl p-7 md:p-8 transition-shadow duration-300 flex flex-col ${
                  c.featured
                    ? "bg-[#2B1D18] text-[#F6F2EA] kb-tracing"
                    : "bg-white border border-[#DED2C2] hover:border-[#B8743C] hover:shadow-[0_20px_40px_rgba(43,29,24,0.06)]"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className={`text-xs font-bold uppercase tracking-[0.22em] ${c.featured ? "text-[#F2D5A3]" : "text-[#B8743C]"}`}>
                    {c.title}
                  </div>
                  {c.tag && (
                    <span className={`shrink-0 text-[10px] font-medium px-2.5 py-1 rounded-full whitespace-nowrap ${c.featured ? "bg-[#F2D5A3] text-[#2B1D18]" : c.tag === "Recurring" ? "bg-[#EFE8DA] text-[#B8743C] border border-[#DED2C2]" : "bg-[#B8743C] text-[#F6F2EA]"}`}>{c.tag}</span>
                  )}
                </div>
                <div className={`mt-4 font-display text-4xl md:text-5xl font-semibold leading-none ${c.featured ? "text-[#F6F2EA]" : "text-[#2A1F1A]"}`}>
                  {c.amount}
                </div>
                <div className={`mt-2 text-sm ${c.featured ? "text-[#E6DCC8]/80" : "text-[#5C4E46]"}`}>{c.cadence}</div>
                <p className={`mt-5 text-sm md:text-base leading-relaxed flex-1 ${c.featured ? "text-[#E6DCC8]/85" : "text-[#5C4E46]"}`}>{c.desc}</p>
                <Button
                  data-testid={`sponsor-cta-${c.id}`}
                  onClick={() => openDonate()}
                  className={`mt-7 rounded-full px-5 h-11 self-start ${
                    c.featured
                      ? "bg-[#F6F2EA] text-[#2A1F1A] hover:bg-[#F2D5A3]"
                      : "bg-[#B8743C] hover:bg-[#99602F] text-[#F6F2EA]"
                  }`}
                >
                  <Heart className="w-4 h-4 mr-2" /> {c.cta}
                </Button>
              </motion.div>
            ))}
          </div>

          {/* Honour a day */}
          <div className="mt-10 kb-reveal rounded-3xl border border-[#DED2C2] bg-gradient-to-br from-[#F2D5A3]/30 to-[#EFE8DA] p-7 md:p-10">
            <div className="grid md:grid-cols-12 gap-6 items-center">
              <div className="md:col-span-8">
                <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#B8743C]">Celebrate or honour a day</span>
                <h3 className="mt-3 font-display text-2xl md:text-3xl lg:text-4xl text-[#2A1F1A] leading-snug">
                  Some people come here to celebrate. Others come during grief. The routes hold space for both.
                </h3>
                <p className="mt-4 text-[#5C4E46] text-sm md:text-base leading-relaxed max-w-2xl">
                  Support a feeding route in honour of a birthday, anniversary, remembrance,
                  milestone or personal moment. Add a dedication if you'd like, or keep it
                  private. We will reply with a note.
                </p>
                <div className="mt-5 flex flex-wrap gap-2 text-xs">
                  {["Birthday", "Anniversary", "In memory", "Milestone", "Recovery", "Just because"].map((o) => (
                    <span key={o} className="px-3 py-1.5 rounded-full bg-white border border-[#DED2C2] text-[#5C4E46]">{o}</span>
                  ))}
                </div>
              </div>
              <div className="md:col-span-4 flex md:justify-end">
                <Button
                  data-testid="honour-day-cta"
                  onClick={() => openDonate()}
                  className="bg-[#2B1D18] hover:bg-[#1d130f] text-[#F6F2EA] rounded-full px-6 h-12 text-base shadow-[0_10px_30px_rgba(43,29,24,0.2)]"
                >
                  <Heart className="w-4 h-4 mr-2" /> Celebrate a special day
                </Button>
              </div>
            </div>
          </div>

          <p className="mt-8 text-center text-sm text-[#5C4E46]">
            Recurring monthly billing is being set up. For now, monthly amounts process as one time gifts. Email us to schedule recurring support directly.
          </p>
        </div>
      </section>

      {/* GALLERY */}
      <section id="gallery" className="py-24 md:py-32 bg-[#EFE8DA]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="kb-reveal flex items-end justify-between gap-6 flex-wrap">
            <div className="max-w-xl">
              <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#B8743C]">Chapter 09 · From the field</span>
              <h2 className="mt-4 font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
                The dogs you'd be feeding.
              </h2>
            </div>
            <a href="https://instagram.com/kaalubhoorifoundation" target="_blank" rel="noopener noreferrer" data-testid="ig-link" className="inline-flex items-center gap-2 text-[#2A1F1A] hover:text-[#B8743C] transition-colors">
              <Instagram className="w-4 h-4" />
              <span className="border-b border-[#2A1F1A] hover:border-[#B8743C] pb-0.5">@kaalubhoorifoundation</span>
            </a>
          </div>

          <div className="mt-12 columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 md:gap-6 [column-fill:_balance]">
            {GALLERY.map((g, i) => (
              <div
                key={i}
                data-testid={`gallery-item-${i}`}
                onMouseMove={handleSpot}
                className="kb-reveal kb-spot relative overflow-hidden rounded-3xl group mb-4 md:mb-6 break-inside-avoid bg-[#EFE8DA]"
              >
                {g.type === "video" ? (
                  <video
                    src={g.src}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.02]"
                  />
                ) : (
                  <img
                    src={g.src}
                    alt={g.alt}
                    loading="lazy"
                    className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.02]"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#2A1F1A]/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>
              </div>
            ))}
          </div>

          <div className="mt-10 kb-reveal flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-white border border-[#DED2C2] px-5 py-4">
            <div className="text-sm md:text-base text-[#2A1F1A] max-w-xl">
              Every face here eats every day because someone is funding it. Be that someone.
            </div>
            <Button
              data-testid="gallery-donate-btn"
              onClick={() => openDonate()}
              className="bg-[#B8743C] hover:bg-[#99602F] text-[#F6F2EA] rounded-full px-5 h-11"
            >
              <Heart className="w-4 h-4 mr-2" /> Fund a route day
            </Button>
          </div>
        </div>
      </section>
      <section className="py-24 md:py-32 bg-[#F6F2EA]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="kb-reveal md:col-span-5">
            <div className="rounded-3xl bg-white border border-[#DED2C2] p-8 md:p-10 flex items-center justify-center min-h-[340px] md:min-h-[460px]">
              <img
                src="https://customer-assets.emergentagent.com/job_bhoori-mission/artifacts/tu01wm1x_WhatsApp%20Image%202025-11-22%20at%209.50.48%20PM.jpeg"
                alt="Kaalu Bhoori Foundation logo"
                className="w-full max-w-sm h-auto object-contain"
                style={{ mixBlendMode: "multiply" }}
              />
            </div>
          </div>
          <div className="kb-reveal md:col-span-7">
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#B8743C]">Chapter 10 · The story behind the name</span>
            <h2 className="mt-4 font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
              For every Kaalu. For every Bhoori.
            </h2>
            <div className="mt-7 space-y-5 text-[#5C4E46] text-base md:text-lg leading-relaxed">
              <p>
                Kaalu Bhoori Foundation is named after the two names almost every Indian
                street knows. Kaalu, the black dog curled up near a tea stall, and Bhoori,
                the brown dog resting quietly outside a shop or under a parked scooter.
              </p>
              <p>
                There is a Kaalu and a Bhoori in nearly every lane. Dogs people often
                reduce to "just another street dog." But to the communities they live in,
                and to the people who care for them, they are individuals with
                personalities, emotions, routines, friendships, fears, and the same desire
                for safety and kindness as any other animal.
              </p>
              <p>
                The foundation was created to challenge that invisibility and lend a hand
                of dignity and care. Kaalu Bhoori Foundation exists for every Kaalu and
                every Bhoori, the dogs who are overlooked, unnamed, underestimated, and
                yet deeply woven into the everyday life of our cities.
              </p>
              <p>
                What began as years of individual feeding and rescue work evolved into a
                structured mission focused on feeding, rescue, sterilisation, vaccination,
                recovery care, and building a more humane model of coexistence between
                cities and community dogs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT / FOOTER */}
      <footer id="contact" className="bg-[#E6DCC8] border-t border-[#C9BCA7]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-5 kb-reveal">
              <div className="flex items-center gap-3">
                <img
                  src="https://customer-assets.emergentagent.com/job_bhoori-mission/artifacts/tu01wm1x_WhatsApp%20Image%202025-11-22%20at%209.50.48%20PM.jpeg"
                  alt="Kaalu Bhoori Foundation"
                  className="h-32 md:h-40 w-auto object-contain"
                  style={{ mixBlendMode: "multiply" }}
                />
              </div>
              <p className="mt-5 text-[#5C4E46] max-w-md leading-relaxed">
                Urban street dog feeding, sterilization and rescue support in Indore,
                Madhya Pradesh. Registered under Sections 12A and 80G.
              </p>
              <Button
                data-testid="footer-donate-btn"
                onClick={() => openDonate()}
                className="mt-8 bg-[#B8743C] hover:bg-[#99602F] text-[#F6F2EA] rounded-full px-6 h-11"
              >
                <Heart className="w-4 h-4 mr-2" /> Donate
              </Button>
            </div>

            <div className="md:col-span-4 kb-reveal">
              <div className="text-xs font-bold uppercase tracking-[0.22em] text-[#B8743C]">Reach us</div>
              <ul className="mt-5 space-y-3 text-[#2A1F1A]">
                <li className="flex items-start gap-3">
                  <Mail className="w-4 h-4 mt-1 text-[#B8743C]" />
                  <a href="mailto:kaalubhoorifoundation@gmail.com" data-testid="footer-email" className="hover:text-[#B8743C] transition-colors break-all">kaalubhoorifoundation@gmail.com</a>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 mt-1 text-[#B8743C]" />
                  <span>Indore, Madhya Pradesh, India</span>
                </li>
                <li className="flex items-start gap-3">
                  <Instagram className="w-4 h-4 mt-1 text-[#B8743C]" />
                  <a href="https://instagram.com/kaalubhoorifoundation" target="_blank" rel="noopener noreferrer" className="hover:text-[#B8743C] transition-colors">@kaalubhoorifoundation</a>
                </li>
                <li className="flex items-start gap-3">
                  <Instagram className="w-4 h-4 mt-1 text-[#B8743C]" />
                  <a href="https://instagram.com/picturekahero" target="_blank" rel="noopener noreferrer" className="hover:text-[#B8743C] transition-colors">@picturekahero</a>
                </li>
              </ul>

              <div className="mt-7 flex items-start gap-4 rounded-2xl bg-white border border-[#DED2C2] p-4">
                <img
                  src={SURBHI_PHOTO}
                  alt="Surbhi, who you'll be writing to"
                  className="w-14 h-14 md:w-16 md:h-16 rounded-full object-cover shrink-0 border border-[#DED2C2]"
                />
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.22em] text-[#B8743C]">If you write to us</div>
                  <p className="mt-1.5 text-sm text-[#5C4E46] leading-relaxed">
                    You'll be writing to Surbhi. Usually with a dog leaning on her shoulder, sometimes two. She reads everything, replies to most of it.
                  </p>
                </div>
              </div>
            </div>

            <div className="md:col-span-3 kb-reveal">
              <div className="text-xs font-bold uppercase tracking-[0.22em] text-[#B8743C]">Explore</div>
              <ul className="mt-5 space-y-2.5 text-[#2A1F1A]">
                <li><a href="#problem" className="hover:text-[#B8743C] transition-colors">The Problem</a></li>
                <li><a href="#how" className="hover:text-[#B8743C] transition-colors">How it works</a></li>
                <li><a href="#impact" className="hover:text-[#B8743C] transition-colors">Impact</a></li>
                <li><a href="#donate" className="hover:text-[#B8743C] transition-colors">Donate</a></li>
                <li><a href="#gallery" className="hover:text-[#B8743C] transition-colors">Gallery</a></li>
              </ul>
            </div>
          </div>

          <div className="mt-14 pt-8 border-t border-[#C9BCA7] pb-20 md:pb-16 space-y-3 text-sm text-[#5C4E46]">
            <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2">
              <div className="font-medium text-[#2A1F1A]">© {new Date().getFullYear()} Kaalu Bhoori Foundation</div>
              <div>On-ground since 2020. Established as Kaalu Bhoori Foundation in 2025. Made with care, in Indore.</div>
            </div>
            <div className="text-xs md:text-sm leading-relaxed text-[#5C4E46]/90 max-w-3xl">
              A Section 8 Company incorporated under the Companies Act, 2013. CIN: U75000MP2025NPL078200. Under the jurisdiction of ROC, Gwalior (Madhya Pradesh). Registered under Sections 12A and 80G of the Income Tax Act.
            </div>
          </div>
        </div>
      </footer>

      {/* Sticky donation bar */}
      <motion.div
        data-testid="sticky-donate-bar"
        initial={{ y: 90 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, delay: 1.5, ease: EASE }}
        className="fixed bottom-0 left-0 right-0 z-30 bg-[#2B1D18]/95 backdrop-blur-md border-t border-[#2B1D18] text-[#F6F2EA] shadow-[0_-12px_30px_rgba(43,29,24,0.18)]"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-10 py-3 md:py-3.5 flex items-center gap-3 md:gap-6">
          <div className="hidden md:flex items-center gap-2 text-[#F2D5A3]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#F2D5A3] animate-pulse"></span>
            <span className="text-[11px] font-bold uppercase tracking-[0.2em]">Tonight's eight routes</span>
          </div>
          <p className="flex-1 text-sm md:text-base leading-snug text-[#E6DCC8]">
            <span className="hidden md:inline">Eight daily feeding and monitoring routes still need support. </span>
            <span className="text-[#F6F2EA] font-medium">Every meal on the street is funded by someone. Could be you tonight.</span>
          </p>
          <div className="flex items-center gap-2">
            <Button
              data-testid="sticky-feed-btn"
              onClick={() => openDonate()}
              variant="ghost"
              className="hidden sm:inline-flex h-10 rounded-full px-4 text-sm text-[#F6F2EA] hover:bg-[#F6F2EA]/10 hover:text-[#F2D5A3]"
            >
              Feed one dog
            </Button>
            <Button
              data-testid="sticky-donate-btn"
              onClick={() => openDonate()}
              className="bg-[#B8743C] hover:bg-[#99602F] text-[#F6F2EA] rounded-full px-5 h-10 text-sm shadow-[0_8px_20px_rgba(184,116,60,0.35)]"
            >
              <Heart className="w-4 h-4 mr-2" /> Support a route
            </Button>
          </div>
        </div>
      </motion.div>

      <DonateModal open={donateOpen} onOpenChange={setDonateOpen} defaultTier={donatePrefill} />
    </div>
  );
}
